import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as api from "../../api/mycore/taskAlertsApi";
import * as actions from "./actions";

// ==================== TASK ALERTS ====================

function* fetchTaskAlerts(action) {
    try {
        const filters = action.payload || {};
        const result = yield call(api.fetchTaskAlerts, filters);
        yield put(actions.fetchTaskAlertsSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchTaskAlertsFailure(e.message));
    }
}

function* fetchTaskAlertDetail(action) {
    try {
        const { alertId } = action.payload;
        const result = yield call(api.fetchTaskAlertDetail, alertId);
        yield put(actions.fetchTaskAlertDetailSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchTaskAlertDetailFailure(e.message));
    }
}

function* fetchTaskAlertSummary(action) {
    try {
        const { organizationId } = action.payload;
        const params = organizationId ? { organization: organizationId } : {};
        const result = yield call(api.fetchTaskAlertSummary, params);
        yield put(actions.fetchTaskAlertSummarySuccess(result.data));
    } catch (e) {
        yield put(actions.fetchTaskAlertSummaryFailure(e.message));
    }
}

function* fetchUpcomingTaskAlerts(action) {
    try {
        const { days, organizationId } = action.payload;
        const result = yield call(api.fetchUpcomingTaskAlerts, days, organizationId);
        yield put(actions.fetchUpcomingTaskAlertsSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchUpcomingTaskAlertsFailure(e.message));
    }
}

function* createTaskAlert(action) {
    try {
        const result = yield call(api.createTaskAlert, action.payload);
        yield put(actions.createTaskAlertSuccess(result.data));
        // Optionally refresh the list
        yield put(actions.fetchTaskAlertsRequest());
    } catch (e) {
        yield put(actions.createTaskAlertFailure(e.message));
    }
}

function* updateTaskAlert(action) {
    try {
        const { alertId, alertData } = action.payload;
        const result = yield call(api.updateTaskAlert, alertId, alertData);
        yield put(actions.updateTaskAlertSuccess(result.data));
    } catch (e) {
        yield put(actions.updateTaskAlertFailure(e.message));
    }
}

function* deleteTaskAlert(action) {
    try {
        const { alertId } = action.payload;
        yield call(api.deleteTaskAlert, alertId);
        yield put(actions.deleteTaskAlertSuccess(alertId));
        // Refresh the list
        yield put(actions.fetchTaskAlertsRequest());
    } catch (e) {
        yield put(actions.deleteTaskAlertFailure(e.message));
    }
}

function* completeTaskAlert(action) {
    try {
        const { alertId, notes } = action.payload;
        const result = yield call(api.completeTaskAlert, alertId, notes);
        yield put(actions.completeTaskAlertSuccess(result.data));
        // Refresh summary after completing
        yield put(actions.fetchTaskAlertSummaryRequest());
    } catch (e) {
        yield put(actions.completeTaskAlertFailure(e.message));
    }
}

function* dismissTaskAlert(action) {
    try {
        const { alertId } = action.payload;
        const result = yield call(api.dismissTaskAlert, alertId);
        yield put(actions.dismissTaskAlertSuccess(result.data));
        // Refresh summary after dismissing
        yield put(actions.fetchTaskAlertSummaryRequest());
    } catch (e) {
        yield put(actions.dismissTaskAlertFailure(e.message));
    }
}

function* reopenTaskAlert(action) {
    try {
        const { alertId } = action.payload;
        const result = yield call(api.reopenTaskAlert, alertId);
        yield put(actions.reopenTaskAlertSuccess(result.data));
        // Refresh summary after reopening
        yield put(actions.fetchTaskAlertSummaryRequest());
    } catch (e) {
        yield put(actions.reopenTaskAlertFailure(e.message));
    }
}

function* updateOverdueAlerts(action) {
    try {
        const result = yield call(api.updateOverdueTaskAlerts);
        yield put(actions.updateOverdueAlertsSuccess(result.data));
        // Refresh the list to show updated statuses
        yield put(actions.fetchTaskAlertsRequest());
    } catch (e) {
        yield put(actions.updateOverdueAlertsFailure(e.message));
    }
}

// ==================== ALERT TEMPLATES ====================

function* fetchAlertTemplates(action) {
    try {
        const filters = action.payload || {};
        const result = yield call(api.fetchAlertTemplates, filters);
        yield put(actions.fetchAlertTemplatesSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchAlertTemplatesFailure(e.message));
    }
}

function* fetchGeneralTemplates(action) {
    try {
        const result = yield call(api.fetchGeneralTemplates);
        yield put(actions.fetchGeneralTemplatesSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchGeneralTemplatesFailure(e.message));
    }
}

function* createAlertTemplate(action) {
    try {
        const result = yield call(api.createAlertTemplate, action.payload);
        yield put(actions.createAlertTemplateSuccess(result.data));
        // Refresh templates list
        yield put(actions.fetchAlertTemplatesRequest());
    } catch (e) {
        yield put(actions.createAlertTemplateFailure(e.message));
    }
}

function* updateAlertTemplate(action) {
    try {
        const { templateId, templateData } = action.payload;
        const result = yield call(api.updateAlertTemplate, templateId, templateData);
        yield put(actions.updateAlertTemplateSuccess(result.data));
    } catch (e) {
        yield put(actions.updateAlertTemplateFailure(e.message));
    }
}

function* deleteAlertTemplate(action) {
    try {
        const { templateId } = action.payload;
        yield call(api.deleteAlertTemplate, templateId);
        yield put(actions.deleteAlertTemplateSuccess(templateId));
        // Refresh templates list
        yield put(actions.fetchAlertTemplatesRequest());
    } catch (e) {
        yield put(actions.deleteAlertTemplateFailure(e.message));
    }
}

// ==================== WATCHERS ====================

export default function* taskAlertsSaga() {
    yield all([
        // Task Alerts
        takeLatest(actions.FETCH_TASK_ALERTS.REQUEST, fetchTaskAlerts),
        takeLatest(actions.FETCH_TASK_ALERT_DETAIL.REQUEST, fetchTaskAlertDetail),
        takeLatest(actions.FETCH_TASK_ALERT_SUMMARY.REQUEST, fetchTaskAlertSummary),
        takeLatest(actions.FETCH_UPCOMING_TASK_ALERTS.REQUEST, fetchUpcomingTaskAlerts),
        takeEvery(actions.CREATE_TASK_ALERT.REQUEST, createTaskAlert),
        takeEvery(actions.UPDATE_TASK_ALERT.REQUEST, updateTaskAlert),
        takeEvery(actions.DELETE_TASK_ALERT.REQUEST, deleteTaskAlert),
        takeEvery(actions.COMPLETE_TASK_ALERT.REQUEST, completeTaskAlert),
        takeEvery(actions.DISMISS_TASK_ALERT.REQUEST, dismissTaskAlert),
        takeEvery(actions.REOPEN_TASK_ALERT.REQUEST, reopenTaskAlert),
        takeEvery(actions.UPDATE_OVERDUE_ALERTS.REQUEST, updateOverdueAlerts),

        // Alert Templates
        takeLatest(actions.FETCH_ALERT_TEMPLATES.REQUEST, fetchAlertTemplates),
        takeLatest(actions.FETCH_GENERAL_TEMPLATES.REQUEST, fetchGeneralTemplates),
        takeEvery(actions.CREATE_ALERT_TEMPLATE.REQUEST, createAlertTemplate),
        takeEvery(actions.UPDATE_ALERT_TEMPLATE.REQUEST, updateAlertTemplate),
        takeEvery(actions.DELETE_ALERT_TEMPLATE.REQUEST, deleteAlertTemplate),
    ]);
}
